import { useEffect, useMemo, useState } from "react";
import styles from "./Answer.module.css";

import { Box, Container, useTheme } from "@mui/material";
import { AiSearchDocumentInfo, LLMResponse } from "../../../api/types/chatTypes";

import { FeedbackRequest, FeedbackSentiment } from "../../../api/types/feedbackTypes";
import { createStringFromMarkdown } from "../../../constants/utils/formatUtils";
import { useConversation } from "../../../contexts/conversationContext";
import FeedbackForm from "../Feedback/Feedback";
import AnswerContent from "./AnswerContent";
import CitationList from "./CitationList";
import FeedbackButtons from "./FeedbackButtons";
import { useAnswerParser } from "../../../hooks/useAnswerParser";

interface Props {
    answer: LLMResponse;
    index: number;
}

export const Answer = ({ answer, index }: Props) => {
    const theme = useTheme();
    const { chatHistory, setChatHistory, onShowCitation } = useConversation();
    const { parseAnswerToHtml, cleanHTML } = useAnswerParser();

    //Feedback UI
    const [isFeedbackOpen, setFeedbackOpen] = useState(false);
    const [feedbackFormSentiment, setFeedbackFormSentiment] = useState<FeedbackSentiment>("negative");

    const openFeedbackDialog = (formSentiment: FeedbackSentiment) => {
        setFeedbackFormSentiment(formSentiment);
        setFeedbackOpen(true);
    };

    // Citations (all across chat, used/unused for this answer)
    const citations = useMemo(
        () =>
            chatHistory
                .flatMap(item => {
                    return [item.response.usedCitations, item.response.unusedCitations].flatMap(item => item);
                })
                .filter(item => item !== undefined),
        [chatHistory]
    );

    const unusedCitations = useMemo(() => answer.unusedCitations, [answer]);
    const usedCitations = useMemo(() => {
        return citations.filter(c => answer.answer.includes(c.id));
    }, [answer, citations]);

    //Parsing + copyable plaintext
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer.answer, citations), [theme, answer.answer, citations]);
    const cleanedHtml = useMemo(() => cleanHTML(parsedAnswer), [parsedAnswer]);
    const plainText = useMemo(() => createStringFromMarkdown(cleanedHtml), [cleanedHtml]);

    //Feedback state in chat history
    const currentFeedback = chatHistory.find(element => element.response === answer)?.feedback || undefined;
    const changeAnswerFeedback = (newFeedback: FeedbackRequest) => {
        setChatHistory(
            chatHistory.map(element => {
                if (element.response !== answer) return element;
                return {
                    ...element,
                    feedback: newFeedback
                };
            })
        );
    };

    useEffect(() => {
        const answerContainer = document.getElementById(`answer-${index}`);
        if (!answerContainer) return;

        const citationSpans = answerContainer.querySelectorAll<HTMLSpanElement>("span[data-tag]");
        const clickHandlers: { span: HTMLSpanElement; handler: (e: MouseEvent) => void }[] = [];

        citationSpans.forEach(span => {
            const handler = () => {
                const tag = span.getAttribute("data-tag");
                const source = citations?.find(src => src.citation_display_name === tag);
                if (source) onShowCitation(source, answer);
            };

            span.addEventListener("click", handler);
            clickHandlers.push({ span, handler });
        });
        return () => {
            clickHandlers.forEach(({ span, handler }) => {
                span.removeEventListener("click", handler);
            });
        };
    }, [parsedAnswer, onShowCitation, index]);

    const onCitationClicked = (source: AiSearchDocumentInfo) => onShowCitation(source, answer);

    return (
        <Container id={`answer-${index}`} className={`${styles.answerContainer}`} sx={{ background: "transparent" }}>
            <AnswerContent answer={answer} parsedAnswer={parsedAnswer} />

            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", width: "100%", alignItems: "flex-end" }}>
                {(usedCitations.length > 0 || unusedCitations) && (
                    <CitationList usedSources={usedCitations} unusedSources={unusedCitations} onCitationClicked={onCitationClicked} />
                )}
                {answer.answerTime && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", width: "100%", alignItems: "flex-end" }}>
                        <FeedbackButtons answer={answer} currentFeedback={currentFeedback} openFeedbackDialog={openFeedbackDialog} textToCopy={plainText} />
                        {isFeedbackOpen && answer.message_id && (
                            <FeedbackForm
                                changeAnswerFeedback={changeAnswerFeedback}
                                currentFeedback={currentFeedback}
                                setFeedbackOpen={setFeedbackOpen}
                                feedbackFormSentiment={feedbackFormSentiment}
                                messageId={answer.message_id}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Container>
    );
};
